/* eslint-disable import/prefer-default-export */
import { getScheduleStatusDisplay, getScheduleTypeDisplay } from "configs/schedules";
import { NONE_REPEATS } from "constants/schedule-monitor.constants";
import { ISchedule, IScheduleRowViewModel } from "types/schedule";

/**
 * Parses the wire's `Date | string | undefined` into a real `Date`, or `null` when
 * absent/unparseable. Never returns an Invalid Date - formatting one throws a RangeError.
 */
const toValidDate = (value: Date | string | undefined): Date | null => {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** "daily" -> "Daily"; a non-repeating schedule ("none") renders as null, not the word "none". */
const formatRepeats = (repeats: string | undefined): string | null => {
    if (!repeats || repeats.toLowerCase() === NONE_REPEATS) {
        return null;
    }
    return repeats.charAt(0).toUpperCase() + repeats.slice(1);
};

/** "DataWarehouseTimeModifier" -> "Data Warehouse Time Modifier" */
const formatModifiers = (modifiers: string[] | undefined): string | null => {
    if (!modifiers || modifiers.length === 0) {
        return null;
    }
    return modifiers.map((modifier) => modifier.replace(/([a-z0-9])([A-Z])/g, "$1 $2")).join(", ");
};

export const toScheduleRowViewModel = (schedule: ISchedule): IScheduleRowViewModel => ({
    id: schedule.id,
    typeCode: schedule.type,
    typeLabel: getScheduleTypeDisplay(schedule.type).label,
    status: schedule.status,
    statusLabel: getScheduleStatusDisplay(schedule.status).label,
    startTime: toValidDate(schedule.recurringConfig?.startDateTime),
    endTime: toValidDate(schedule.recurringConfig?.endingDate),
    repeats: formatRepeats(schedule.recurringConfig?.repeats),
    repeatsCode: schedule.recurringConfig?.repeats?.toLowerCase() ?? null,
    modifier: formatModifiers(schedule.dataModifier),
    createdAt: toValidDate(schedule.createdAt),
    data: (schedule.data as unknown as Record<string, unknown>) ?? null,
    hasNextTask: null,
});

export default toScheduleRowViewModel;
